import { Area, CartesianGrid, ComposedChart, Line, ReferenceLine, Tooltip, XAxis, YAxis } from "recharts";

import { ChartFrame } from "@/components/data";
import { formatGrainTick, type GrainValue } from "@/lib/time-range";

import type { SeriesPoint } from "../api";
import { formatTileValue, formatTtuValue, formatUnits, type Grain } from "../format";

const GRAIN_NOUN: Record<Grain, string> = {
  daily: "day",
  weekly: "week",
  monthly: "month",
};

export function trendValueFormat(
  metric: "growth" | "units" | "value",
  unitLabel?: string,
): (value: number | null) => string {
  if (metric === "units") return formatUnits;
  if (metric === "growth") return (value) => formatTileValue(value, "%");
  return (value) => formatTtuValue(value, unitLabel ?? "");
}

/**
 * One trend line - the growth rate or the volume, never both on one axis.
 *
 * Charting goes through `ChartFrame` - the one charting seam (I-7). A module
 * never picks its own library, which is exactly how three chart idioms ended up
 * across the estate this portal exists to end.
 *
 * The empty message names the GRAIN. "No data" on a daily view, when monthly
 * has plenty, sends a reader looking for an outage that is really an
 * unbackfilled series.
 */
export function UnitsTrend({
  points,
  grain,
  metric,
  unitLabel,
  valueLabel,
  title,
  emptyMessage,
}: {
  points: SeriesPoint[];
  grain: Grain;
  metric: "growth" | "units" | "value";
  unitLabel?: string;
  valueLabel?: string;
  title?: string;
  emptyMessage?: string;
}) {
  const data = points.map((point) => ({ period: point.m, value: point.v }));
  const isUnits = metric === "units";
  const format = trendValueFormat(metric, unitLabel);

  return (
    <ChartFrame
      title={title ?? `${isUnits ? "Units sold" : "YoY unit growth"} by ${GRAIN_NOUN[grain]}`}
      titleVisible
      pointCount={data.length}
      emptyMessage={
        emptyMessage ??
        `No ${grain} points yet - the producer has not backfilled this grain. Try another period.`
      }
    >
      <ComposedChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-ks-grey-4)" />
        <XAxis
          dataKey="period"
          tick={{ fontSize: 12 }}
          stroke="var(--color-ks-grey-1)"
          tickFormatter={(value: string) => formatGrainTick(value, grain as GrainValue)}
        />
        <YAxis
          tick={{ fontSize: 12 }}
          stroke="var(--color-ks-grey-1)"
          tickFormatter={(value: number) => format(value)}
          width={metric === "growth" ? 52 : 64}
          allowDecimals={false}
        />
        <Tooltip
          // Recharts types a tooltip value as possibly-undefined. Coercing it to
          // a number would print "0" for a point that has no value, so unknown
          // values go through the same stated-missing formatter as other figures.
          formatter={(value: unknown) => [
            format(typeof value === "number" ? value : null),
            metric === "growth" ? "YoY growth" : metric === "value" ? valueLabel ?? unitLabel ?? "Units" : unitLabel ?? "Units",
          ]}
          cursor={{ strokeDasharray: "3 3" }}
          contentStyle={{
            background: "var(--color-ks-surface)",
            border: "1px solid var(--color-border)",
            borderRadius: "var(--radius-ks-sm)",
            fontSize: 12,
          }}
        />
        {!isUnits && <ReferenceLine y={0} stroke="var(--color-ks-grey-1)" />}
        <Area
          type="linear"
          dataKey="value"
          stroke="none"
          fill="var(--color-ks-purple)"
          fillOpacity={0.12}
          // See YieldTrend: the shared dataKey is for the fill only, not a
          // second tooltip row of the same figure.
          tooltipType="none"
          isAnimationActive={false}
        />
        <Line
          type="linear"
          dataKey="value"
          stroke="var(--color-ks-purple)"
          strokeWidth={2}
          dot={{ r: 3 }}
          activeDot={{ r: 5 }}
          isAnimationActive={false}
        />
      </ComposedChart>
    </ChartFrame>
  );
}
